import { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Calendar, User, FileText, Loader2, Search } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useBusiness } from "@/hooks/use-business";
import { useAppointments } from "@/hooks/use-appointments";
import { useStaff } from "@/hooks/use-staff";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export default function VisitNotes() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { business } = useBusiness();
  const { appointments, loading: appointmentsLoading } = useAppointments();
  const { staff } = useStaff();
  const { toast } = useToast();

  // Check if we're in view-only mode (from URL search params)
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get("view") === "true";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointment, setAppointment] = useState<any>(null);
  const [visitNote, setVisitNote] = useState<any>(null);
  const [providerId, setProviderId] = useState<string>("");

  // Form fields
  const [reasonForVisit, setReasonForVisit] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnosisType, setDiagnosisType] = useState<"text" | "dropdown">("text");
  const [treatmentNotes, setTreatmentNotes] = useState("");
  const [followUpInstructions, setFollowUpInstructions] = useState("");
  const [nextAppointmentSuggestion, setNextAppointmentSuggestion] = useState("");

  // Common diagnoses (can be customized per clinic)
  const commonDiagnoses = [
    "Hypertension",
    "Diabetes",
    "Upper Respiratory Infection",
    "Common Cold",
    "Flu",
    "Headache",
    "Back Pain",
    "Fever",
    "Cough",
    "Sore Throat",
    "Other",
  ];

  const [appointmentsList, setAppointmentsList] = useState<any[]>([]);
  const [visitNotesMap, setVisitNotesMap] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!appointmentId) {
      // List view - load appointments that need visit notes
      const loadAppointmentsList = async () => {
        // Wait for appointments to load
        if (appointmentsLoading) {
          return;
        }

        try {
          setLoading(true);
          
          // Get completed/past appointments - use end_datetime to check if appointment has ended
          const now = new Date();
          const completedAppointments = appointments.filter(
            (apt) => {
              // Include appointments that have ended (end_datetime < now)
              // or appointments with completed status
              const hasEnded = apt.end_datetime ? new Date(apt.end_datetime) < now : false;
              const isCompleted = apt.status === "completed";
              return hasEnded || isCompleted;
            }
          );

          // Load visit notes for these appointments
          const appointmentIds = completedAppointments.map((apt) => apt.id);
          if (appointmentIds.length > 0) {
            const { data: notes, error: notesError } = await (supabase as any)
              .from("visit_notes")
              .select("*")
              .in("appointment_id", appointmentIds);

            if (notesError) throw notesError;

            const notesMap: Record<string, any> = {};
            notes?.forEach((note) => {
              notesMap[note.appointment_id] = note;
            });
            setVisitNotesMap(notesMap);
          }

          setAppointmentsList(completedAppointments.sort((a, b) => 
            new Date(b.start_datetime).getTime() - new Date(a.start_datetime).getTime()
          ));
        } catch (error) {
          console.error("Error loading appointments:", error);
        } finally {
          setLoading(false);
        }
      };

      loadAppointmentsList();
    }
    
    // Re-run when appointments change or finish loading
  }, [appointmentId, appointments, appointmentsLoading]);

  useEffect(() => {
    if (appointmentId) {
      const loadData = async () => {
        try {
          setLoading(true);

          // Directly query the appointment by ID instead of relying on the appointments array
          // This ensures we can find it even if it's filtered out or not loaded yet
          const { data: apt, error: aptError } = await (supabase as any)
            .from("appointments")
            .select(`
              *,
              customer:customers(*),
              service:services(*),
              staffMember:staff(*)
            `)
            .eq("id", appointmentId)
            .single();

          if (aptError || !apt) {
            toast({
              title: "Appointment not found",
              description: "The appointment could not be found.",
              variant: "destructive",
            });
            navigate("/app/appointments");
            return;
          }

          setAppointment(apt);

          // Set default provider (staff assigned to appointment or current user)
          if (apt.staff_id) {
            setProviderId(apt.staff_id);
          } else if (staff.length > 0) {
            setProviderId(staff[0].id);
          }

          // Load existing visit note if any
          const { data: existingNote, error: noteError } = await (supabase as any)
            .from("visit_notes")
            .select("*")
            .eq("appointment_id", appointmentId)
            .maybeSingle();

          if (noteError && noteError.code !== "PGRST116") {
            throw noteError;
          }

          if (existingNote) {
            setVisitNote(existingNote);
            setProviderId(existingNote.provider_id);
            // Map SOAP fields to our simpler format
            setReasonForVisit(existingNote.subjective || "");
            setDiagnosis(existingNote.assessment || "");
            setTreatmentNotes(existingNote.plan || "");
            setFollowUpInstructions(existingNote.notes || "");
            setNextAppointmentSuggestion(existingNote.objective || ""); // Load next appointment suggestion from objective field
          }
        } catch (error) {
          console.error("Error loading visit note:", error);
          toast({
            title: "Error",
            description: "Failed to load visit note data.",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }
  }, [appointmentId, staff, navigate, toast]);

  const handleSave = async () => {
    if (!appointmentId || !providerId) {
      toast({
        title: "Missing information",
        description: "Please select a provider.",
        variant: "destructive",
      });
      return;
    }

    if (!reasonForVisit.trim()) {
      toast({
        title: "Required field",
        description: "Reason for visit is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);

      const noteData = {
        appointment_id: appointmentId,
        provider_id: providerId,
        subjective: reasonForVisit,
        assessment: diagnosis,
        plan: treatmentNotes,
        notes: followUpInstructions,
        objective: nextAppointmentSuggestion, // Store next appointment suggestion in objective field
      };

      if (visitNote) {
        // Update existing note
        const { error } = await (supabase as any)
          .from("visit_notes")
          .update(noteData)
          .eq("id", visitNote.id);

        if (error) throw error;

        toast({
          title: "Visit note updated",
          description: "The visit note has been saved successfully.",
        });
      } else {
        // Create new note
        const { error } = await (supabase as any).from("visit_notes").insert(noteData);

        if (error) throw error;

        toast({
          title: "Visit note created",
          description: "The visit note has been saved successfully.",
        });
      }

      // Reload the visit note to show it was saved
      const { data: savedNote, error: reloadError } = await (supabase as any)
        .from("visit_notes")
        .select("*")
        .eq("appointment_id", appointmentId)
        .maybeSingle();

      if (!reloadError && savedNote) {
        setVisitNote(savedNote);
      }

      // Send follow-up instructions email to patient if provided
      if (followUpInstructions.trim() || nextAppointmentSuggestion.trim()) {
        const customer = appointment.customer;
        if (customer?.email) {
          // Fire-and-forget email notification (don't block UI if it fails)
          supabase.functions
            .invoke("send-email", {
              body: {
                type: "visit_notes_followup",
                appointmentId: appointmentId,
                followUpInstructions: followUpInstructions.trim(),
                nextAppointmentSuggestion: nextAppointmentSuggestion.trim(),
                diagnosis: diagnosis.trim(),
                treatmentNotes: treatmentNotes.trim(),
              },
            })
            .then(({ error }) => {
              if (error) {
                console.warn("send-email visit_notes_followup failed:", error);
              } else {
                toast({
                  title: "Follow-up instructions sent",
                  description: "Patient has been notified via email.",
                });
              }
            })
            .catch((e) => console.warn("send-email visit_notes_followup crashed:", e));
        }
      }

      // Show success message
      toast({
        title: visitNote ? "Visit note updated" : "Visit note created",
        description: "The visit note has been saved successfully.",
      });
    } catch (error: any) {
      console.error("Error saving visit note:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save visit note.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  // List view (no appointmentId)
  if (!appointmentId) {
    const filteredAppointments = appointmentsList.filter((apt) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        apt.customer?.name?.toLowerCase().includes(query) ||
        apt.service?.name?.toLowerCase().includes(query) ||
        apt.confirmation_code?.toLowerCase().includes(query)
      );
    });

    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader
          title="Visit Notes"
          description="Document patient visit details"
        />

        <div className="space-y-6">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by patient name, service, or confirmation code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Appointments List */}
          <div className="space-y-3">
            {filteredAppointments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery
                      ? "No appointments found matching your search."
                      : "No completed appointments found."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredAppointments.map((apt) => {
                const hasNote = !!visitNotesMap[apt.id];
                const customer = apt.customer;
                return (
                  <Card key={apt.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <User className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">
                                {customer?.name || "Unknown Patient"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {format(
                                  new Date(apt.start_datetime),
                                  "MMM d, yyyy 'at' h:mm a"
                                )}
                              </p>
                            </div>
                          </div>
                          {apt.service && (
                            <p className="text-sm text-muted-foreground ml-8">
                              {apt.service.name}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {hasNote && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Note Added
                            </Badge>
                          )}
                          {hasNote ? (
                            <>
                              <Button
                                asChild
                                variant="outline"
                              >
                                <Link to={`/app/visit-notes/${apt.id}?view=true`}>
                                  <FileText className="mr-2 h-4 w-4" />
                                  View Note
                                </Link>
                              </Button>
                              <Button
                                asChild
                                variant="default"
                              >
                                <Link to={`/app/visit-notes/${apt.id}`}>
                                  Edit Note
                                </Link>
                              </Button>
                            </>
                          ) : (
                            <Button
                              asChild
                              variant="default"
                            >
                              <Link to={`/app/visit-notes/${apt.id}`}>
                                Add Note
                              </Link>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </AppShell>
    );
  }

  if (!appointment) {
    return null;
  }

  const customer = appointment.customer;
  const selectedProvider = staff.find((s) => s.id === providerId);

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title={viewOnly ? "View Visit Note" : "Visit Notes"}
        description={viewOnly ? "Read-only view of patient visit details" : "Document patient visit details"}
        action={
          viewOnly && visitNote ? (
            <Button
              onClick={() => navigate(`/app/visit-notes/${appointmentId}`)}
            >
              Edit Note
            </Button>
          ) : null
        }
      />

      <div className="space-y-6">
        {/* Appointment Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Appointment Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Patient</p>
                  <p className="font-medium">
                    {customer?.name || "Unknown Patient"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date & Time</p>
                  <p className="font-medium">
                    {format(
                      new Date(appointment.start_datetime),
                      "MMM d, yyyy 'at' h:mm a"
                    )}
                  </p>
                </div>
              </div>
              {appointment.service && (
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Service</p>
                    <p className="font-medium">{appointment.service.name}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Visit Notes Form or View */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Visit Documentation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {viewOnly ? (
              /* Read-only view */
              <>
                {/* Provider */}
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <p className="text-sm font-medium">
                    {selectedProvider?.name || "Not specified"}
                  </p>
                </div>

                {/* Reason for Visit */}
                <div className="space-y-2">
                  <Label>Reason for Visit</Label>
                  <p className="text-sm whitespace-pre-wrap">
                    {reasonForVisit || <span className="text-muted-foreground italic">Not specified</span>}
                  </p>
                </div>

                {/* Diagnosis */}
                {diagnosis && (
                  <div className="space-y-2">
                    <Label>Diagnosis</Label>
                    <p className="text-sm whitespace-pre-wrap">{diagnosis}</p>
                  </div>
                )}

                {/* Treatment / Prescription Notes */}
                {treatmentNotes && (
                  <div className="space-y-2">
                    <Label>Treatment / Prescription Notes</Label>
                    <p className="text-sm whitespace-pre-wrap">{treatmentNotes}</p>
                  </div>
                )}

                {/* Follow-up Instructions */}
                {followUpInstructions && (
                  <div className="space-y-2">
                    <Label>Follow-up Instructions</Label>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm whitespace-pre-wrap text-blue-900">{followUpInstructions}</p>
                    </div>
                  </div>
                )}

                {/* Next Appointment Suggestion */}
                {nextAppointmentSuggestion && (
                  <div className="space-y-2">
                    <Label>Next Appointment Suggestion</Label>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-900">{nextAppointmentSuggestion}</p>
                    </div>
                  </div>
                )}

                {!reasonForVisit && !diagnosis && !treatmentNotes && !followUpInstructions && !nextAppointmentSuggestion && (
                  <p className="text-sm text-muted-foreground italic text-center py-8">
                    No visit note details available.
                  </p>
                )}
              </>
            ) : (
              /* Editable form */
              <>
                {/* Provider Selection */}
                <div className="space-y-2">
                  <Label htmlFor="provider">Provider *</Label>
                  <Select value={providerId} onValueChange={setProviderId} disabled={viewOnly}>
                    <SelectTrigger id="provider">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff
                        .filter((s) => s.active)
                        .map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason for Visit */}
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Visit *</Label>
                  <Textarea
                    id="reason"
                    value={reasonForVisit}
                    onChange={(e) => setReasonForVisit(e.target.value)}
                    placeholder="Enter the reason for this visit..."
                    rows={3}
                    className="resize-none"
                    disabled={viewOnly}
                  />
                </div>

                {/* Diagnosis */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="diagnosis">Diagnosis</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant={diagnosisType === "text" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDiagnosisType("text")}
                        disabled={viewOnly}
                      >
                        Free Text
                      </Button>
                      <Button
                        type="button"
                        variant={diagnosisType === "dropdown" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDiagnosisType("dropdown")}
                        disabled={viewOnly}
                      >
                        Select from List
                      </Button>
                    </div>
                  </div>
                  {diagnosisType === "text" ? (
                    <Textarea
                      id="diagnosis"
                      value={diagnosis}
                      onChange={(e) => setDiagnosis(e.target.value)}
                      placeholder="Enter diagnosis..."
                      rows={2}
                      className="resize-none"
                      disabled={viewOnly}
                    />
                  ) : (
                    <Select value={diagnosis} onValueChange={setDiagnosis} disabled={viewOnly}>
                      <SelectTrigger id="diagnosis">
                        <SelectValue placeholder="Select diagnosis" />
                      </SelectTrigger>
                      <SelectContent>
                        {commonDiagnoses.map((diag) => (
                          <SelectItem key={diag} value={diag}>
                            {diag}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Treatment / Prescription Notes */}
                <div className="space-y-2">
                  <Label htmlFor="treatment">Treatment / Prescription Notes</Label>
                  <Textarea
                    id="treatment"
                    value={treatmentNotes}
                    onChange={(e) => setTreatmentNotes(e.target.value)}
                    placeholder="Enter treatment plan, prescriptions, or other treatment notes..."
                    rows={4}
                    className="resize-none"
                    disabled={viewOnly}
                  />
                </div>

                {/* Follow-up Instructions */}
                <div className="space-y-2">
                  <Label htmlFor="followup">Follow-up Instructions</Label>
                  <Textarea
                    id="followup"
                    value={followUpInstructions}
                    onChange={(e) => setFollowUpInstructions(e.target.value)}
                    placeholder="Enter follow-up instructions for the patient..."
                    rows={3}
                    className="resize-none"
                    disabled={viewOnly}
                  />
                </div>

                {/* Next Appointment Suggestion */}
                <div className="space-y-2">
                  <Label htmlFor="nextAppointment">Next Appointment Suggestion</Label>
                  <Input
                    id="nextAppointment"
                    value={nextAppointmentSuggestion}
                    onChange={(e) => setNextAppointmentSuggestion(e.target.value)}
                    placeholder="e.g., 'Follow-up in 2 weeks' or 'Schedule in 1 month'"
                    disabled={viewOnly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Suggest when the patient should return for a follow-up
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Success Message */}
        {visitNote && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-900">Visit Note Saved</p>
                    <p className="text-sm text-green-700">
                      This visit note has been documented and saved.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate("/app/visit-notes")}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  View All Notes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate("/app/appointments")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Appointments
          </Button>
          <div className="flex gap-2">
            {viewOnly ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate("/app/visit-notes")}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View All Notes
                </Button>
                <Button
                  onClick={() => navigate(`/app/visit-notes/${appointmentId}`)}
                >
                  Edit Note
                </Button>
              </>
            ) : (
              <>
                {visitNote && (
                  <Button
                    variant="outline"
                    onClick={() => navigate("/app/visit-notes")}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    View All Notes
                  </Button>
                )}
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {visitNote ? "Update Visit Note" : "Save Visit Note"}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
